import { useState, useEffect, useRef } from 'react';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import { API_URL } from '../../../config/api';
import EmailShipment from '../EmailShipment/EmailShipment';

// PDF Styles for packing slip
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    textAlign: 'center',
    marginBottom: 20,
    borderBottom: '2px solid #34495e',
    paddingBottom: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  shipmentNumber: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    borderBottom: '1px solid #bdc3c7',
    paddingBottom: 4,
  },
  infoGrid: {
    marginBottom: 10,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  infoLabel: {
    width: '30%',
    fontWeight: 'bold',
  },
  infoValue: {
    width: '70%',
  },
  table: {
    marginTop: 10,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#34495e',
    color: 'white',
    padding: 6,
    fontWeight: 'bold',
    fontSize: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1px solid #bdc3c7',
    padding: 5,
    fontSize: 9,
  },
  tableRowEven: {
    backgroundColor: '#ecf0f1',
  },
  tableFooter: {
    flexDirection: 'row',
    backgroundColor: '#ecf0f1',
    padding: 6,
    fontWeight: 'bold',
    borderTop: '2px solid #34495e',
  },
  colNumber: { width: '6%' },
  colBarcode: { width: '18%' },
  colWigType: { width: '22%' },
  colCustomer: { width: '22%' },
  colInstructions: { width: '22%' },
  colWeight: { width: '10%', textAlign: 'right' },
  footer: {
    marginTop: 25,
    paddingTop: 15,
    borderTop: '1px solid #bdc3c7',
  },
  signatureSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  signatureLine: {
    width: '45%',
    paddingTop: 15,
  },
  signatureText: {
    borderBottom: '1px solid #2c3e50',
    paddingBottom: 5,
    fontSize: 10,
  },
});

// PDF Document Component
const PackingSlipPDF = ({ data }) => (
  <Document>
    <Page size="LETTER" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        {/* <Text style={styles.title}>PACKING SLIP</Text> */}
        <Text style={styles.shipmentNumber}>Shipment #{data.shipmentDetails.shipmentId}</Text>
      </View>

      {/* Shipping Information */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Shipping Information</Text>
        <View style={styles.infoGrid}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Ship To:</Text>
            <Text style={styles.infoValue}>{data.shipmentDetails.ShippingToName}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Ship Date:</Text>
            <Text style={styles.infoValue}>
              {data.shipmentDetails.ShipOutDate
                ? new Date(data.shipmentDetails.ShipOutDate).toLocaleDateString()
                : 'N/A'}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Courier:</Text>
            <Text style={styles.infoValue}>{data.shipmentDetails.CourrierName || 'N/A'}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Tracking #:</Text>
            <Text style={styles.infoValue}>{data.shipmentDetails.TrackingNumber || 'N/A'}</Text>
          </View>
          {data.shipmentDetails.SpecialInstructions && (
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Special Instructions:</Text>
              <Text style={styles.infoValue}>{data.shipmentDetails.SpecialInstructions}</Text>
            </View>
          )}
        </View>
      </View>

      {/* Wigs Table */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Items in Shipment ({data.wigs.length})</Text>
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={styles.colNumber}>#</Text>
            <Text style={styles.colBarcode}>Barcode</Text>
            <Text style={styles.colWigType}>Wig Type</Text>
            <Text style={styles.colCustomer}>Customer</Text>
            <Text style={styles.colInstructions}>Instructions</Text>
            <Text style={styles.colWeight}>Weight (g)</Text>
          </View>

          {/* Table Rows */}
          {data.wigs.map((wig, index) => (
            <View
              key={wig.Number}
              style={[styles.tableRow, index % 2 === 1 ? styles.tableRowEven : {}]}
            >
              <Text style={styles.colNumber}>{wig.Number}</Text>
              <Text style={styles.colBarcode}>{wig.Wig_Bar_Code}</Text>
              <Text style={styles.colWigType}>{wig.OrderWigType}</Text>
              <Text style={styles.colCustomer}>{wig.OrderCustomerName}</Text>
              <Text style={styles.colInstructions}>{wig.ShippingInstructions || '-'}</Text>
              <Text style={styles.colWeight}>
                {wig.WigWeight ? parseFloat(wig.WigWeight).toFixed(3) : '0.000'}
              </Text>
            </View>
          ))}

          {/* Table Footer */}
          <View style={styles.tableFooter}>
            <Text style={{ width: '90%', textAlign: 'right' }}>Total Weight:</Text>
            <Text style={styles.colWeight}>{data.totalWeight} g</Text>
          </View>
        </View>
      </View>

    
    </Page>
  </Document>
);

// Main Component
const ShippingPrintView = ({ shipmentId, onClose }) => {
  const [shipmentData, setShipmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printTriggered, setPrintTriggered] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const hasOpenedRef = useRef(false);
  const iframeRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => {
    if (shipmentId && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      fetchShipmentData();
    }

    // Cleanup function - remove iframe when component unmounts
    return () => {
      if (iframeRef.current) {
        try {
          document.body.removeChild(iframeRef.current);
        } catch (e) {
          console.error('Error removing iframe:', e);
        }
      }
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
      }
    };
  }, [shipmentId]);

  const fetchShipmentData = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_URL}/api/rachels-room/shipping-print/${shipmentId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch shipment data');
      }

      const data = await response.json();
      setShipmentData(data);
      setLoading(false);

      // Automatically open PDF
      await openPDF(data);
    } catch (err) {
      console.error('Error fetching shipment data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const openPDF = async (data) => {
    try {
      const doc = <PackingSlipPDF data={data} />;
      const asPdf = pdf(doc);
      const blob = await asPdf.toBlob();
      const url = URL.createObjectURL(blob);

      // Store URL for cleanup
      urlRef.current = url;

      // Create hidden iframe to trigger print dialog in same tab
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);

      // Store iframe reference for cleanup
      iframeRef.current = iframe;

      // Wait for iframe to load, then trigger print
      iframe.onload = function() {
        // Add a small delay to ensure PDF is fully loaded
        setTimeout(() => {
          try {
            setPrintTriggered(true);
            iframe.contentWindow.print();

            // IMPORTANT: Don't remove iframe - it needs to stay alive for print dialog
            // It will be cleaned up when modal is closed

          } catch (printError) {
            console.error('Error triggering print:', printError);
            // Fallback: open in new window if iframe print fails
            window.open(url, '_blank');
            // Clean up immediately on error
            try {
              document.body.removeChild(iframe);
            } catch (removeError) {
              console.error('Error removing iframe:', removeError);
            }
            URL.revokeObjectURL(url);
            if (onClose) {
              onClose();
            }
          }
        }, 500);
      };

      // Handle iframe load errors
      iframe.onerror = function(error) {
        console.error('Error loading PDF in iframe:', error);
        setError('Failed to load PDF');
        URL.revokeObjectURL(url);
        if (onClose) {
          onClose();
        }
      };
    } catch (error) {
      console.error('Error opening PDF:', error);
      setError(`Failed to generate PDF: ${error.message}`);
      if (onClose) {
        onClose();
      }
    }
  };

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p style={{ color: 'red' }}>Error: {error}</p>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              padding: '12px 30px',
              backgroundColor: '#95a5a6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '16px',
              marginTop: '20px',
            }}
          >
            Close
          </button>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Preparing shipping label for printing...</p>
      </div>
    );
  }

  const handleEmailClick = (e) => {
    e.stopPropagation();
    setShowEmailModal(true);
  };

  // Show message with close button after print is triggered
  return (
    <>
      <div
        style={{
          padding: '40px',
          textAlign: 'center',
          userSelect: 'none'
        }}
      >
        <p style={{ marginBottom: '15px', fontSize: '16px' }}>
          {printTriggered ? '✓ Print dialog opened' : 'Processing...'}
        </p>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '20px' }}>
          <button
            onClick={handleEmailClick}
            style={{
              padding: '12px 24px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            📧 Email Packing Slip
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#4b5563'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6b7280'}
          >
            Close
          </button>
        </div>

        <p style={{ fontSize: '13px', color: '#888', marginTop: '20px' }}>
          Click "Close" to dismiss or "Email" to send the packing slip
        </p>
      </div>

      {/* Email Modal */}
      <EmailShipment
        isOpen={showEmailModal}
        shipmentId={shipmentId}
        shipmentDetails={shipmentData?.shipmentDetails}
        wigs={shipmentData?.wigs || []}
        wigCount={shipmentData?.wigs?.length || 0}
        onClose={() => setShowEmailModal(false)}
        onSuccess={(result) => {
          console.log('Email sent successfully:', result);
        }}
        onError={(err) => {
          console.error('Email failed:', err);
        }}
      />
    </>
  );
};

export default ShippingPrintView;
